import { Controller } from "@hotwired/stimulus"

const ESTIMATE_EXPIRY_SECONDS = 5

export default class extends Controller {
  static targets = ["buyTab", "sellTab", "buyPanel", "sellPanel", "outcome", "amount", "amountDisplay", "shares", "estimateArea", "submitBtn", "expiryIndicator", "details", "detailsIcon"]
  static values = { slug: String, activeTab: { type: String, default: "buy" }, initialOutcome: { type: String, default: "" } }

  connect() {
    this.detailsVisible = false
    this.showTab(this.activeTabValue)

    this._pricesChangedHandler = this._onPricesChanged.bind(this)
    window.addEventListener("mulatrade:prices-changed", this._pricesChangedHandler)

    if (this.initialOutcomeValue) {
      const btn = this.outcomeTargets.find(el => el.dataset.outcome === this.initialOutcomeValue)
      if (btn) {
        btn.classList.remove("ring-accent")
        btn.click()
      }
    }
  }

  disconnect() {
    this._clearExpiryTimer()
    if (this._pricesChangedHandler) {
      window.removeEventListener("mulatrade:prices-changed", this._pricesChangedHandler)
    }
    if (this._refreshTimer) clearTimeout(this._refreshTimer)
  }

  switchToBuy() {
    this.activeTabValue = "buy"
    this.showTab("buy")
  }

  switchToSell() {
    this.activeTabValue = "sell"
    this.showTab("sell")
  }

  showTab(tab) {
    const isBuy = tab === "buy"
    this.buyTabTarget.classList.toggle("bg-accent", isBuy)
    this.buyTabTarget.classList.toggle("text-white", isBuy)
    this.buyTabTarget.classList.toggle("text-slate-400", !isBuy)
    this.sellTabTarget.classList.toggle("bg-accent", !isBuy)
    this.sellTabTarget.classList.toggle("text-white", !isBuy)
    this.sellTabTarget.classList.toggle("text-slate-400", isBuy)
    this.buyPanelTarget.classList.toggle("hidden", !isBuy)
    this.sellPanelTarget.classList.toggle("hidden", isBuy)
  }

  selectOutcome(event) {
    const btn = event.currentTarget
    const outcome = btn.dataset.outcome
    const alreadySelected = btn.classList.contains("ring-accent")

    this.outcomeTargets.forEach(el => el.classList.remove("ring-2", "ring-inset", "ring-accent"))

    const hiddenId = this.activeTabValue === "buy" ? "buy-outcome" : "sell-outcome"
    const hiddenInput = document.getElementById(hiddenId)

    if (alreadySelected) {
      if (hiddenInput) hiddenInput.value = ""
      const frameId = `${this.activeTabValue}-estimate-preview`
      const frame = document.getElementById(frameId)
      if (frame) frame.innerHTML = `<div class="text-sm text-slate-500 text-center py-2">Selecciona resultado y monto para ver estimación</div>`
      this._clearExpiryTimer()
      return
    }

    btn.classList.add("ring-2", "ring-inset", "ring-accent")
    if (hiddenInput) hiddenInput.value = outcome

    // Auto-fill shares input with exact position value when selecting sell outcome
    if (this.activeTabValue === "sell" && this.hasSharesTarget) {
      const exactShares = btn.dataset.shares
      if (exactShares) this.sharesTarget.value = exactShares
    }

    this.fetchEstimate()
  }

  formatAmount() {
    const raw = this.amountDisplayTarget.value.replace(/\D/g, "")
    const pesos = parseInt(raw) || 0
    this.amountDisplayTarget.value = pesos > 0 ? `$${pesos.toLocaleString("es-CO")}` : ""
    this.amountTarget.value = pesos * 100
    this.updateEstimate()
  }

  toggleDetails() {
    this.detailsVisible = !this.detailsVisible
    this.applyDetailsState()
  }

  detailsTargetConnected() {
    this.applyDetailsState()
  }

  applyDetailsState() {
    if (!this.hasDetailsTarget) return
    this.detailsTarget.classList.toggle("hidden", !this.detailsVisible)
    if (this.hasDetailsIconTarget) {
      this.detailsIconTarget.classList.toggle("rotate-180", this.detailsVisible)
    }
  }

  updateEstimate() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchEstimate(), 300)
  }

  async fetchEstimate() {
    const selectedOutcome = this.outcomeTargets.find(el => el.classList.contains("ring-accent"))
    if (!selectedOutcome) return

    const outcome = selectedOutcome.dataset.outcome
    const tradeType = this.activeTabValue

    let params = `outcome=${outcome}&trade_type=${tradeType}`
    if (tradeType === "buy" && this.hasAmountTarget) {
      const amountCents = this.amountTarget.value
      if (!amountCents || amountCents === "0") return
      params += `&amount_cents=${amountCents}`
    } else if (tradeType === "sell" && this.hasSharesTarget) {
      const shares = this.sharesTarget.value
      if (!shares) return
      params += `&shares=${shares}`
    }

    this._showEstimateSkeleton()

    try {
      const response = await fetch(`/mercados/${this.slugValue}/estimacion?${params}`, {
        headers: { "Accept": "text/vnd.turbo-stream.html" }
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        requestAnimationFrame(() => this._onEstimateRendered())
      }
    } catch (e) {
      // Silently handle network errors
    }
  }

  // --- Estimate expiry ---

  _onEstimateRendered() {
    this._setSubmitEnabled(true)
    this._startExpiryCountdown()
  }

  _startExpiryCountdown() {
    this._clearExpiryTimer()
    this._expiryRemaining = ESTIMATE_EXPIRY_SECONDS

    this._updateExpiryIndicator()
    this._expiryInterval = setInterval(() => {
      this._expiryRemaining--
      this._updateExpiryIndicator()

      if (this._expiryRemaining <= 0) {
        this._expireEstimate()
      }
    }, 1000)
  }

  _updateExpiryIndicator() {
    const indicators = this.expiryIndicatorTargets
    if (!indicators.length) return

    const active = this._getActiveIndicator()
    if (!active) return

    if (this._expiryRemaining > 0) {
      const dot = active.querySelector("span:first-child")
      const text = active.querySelector("span:last-child")
      if (dot) {
        dot.classList.remove("bg-amber-400", "bg-red-400")
        dot.classList.add("bg-green-400", "animate-pulse")
      }
      if (text) text.textContent = `Cotización vigente (${this._expiryRemaining}s)`
    }
  }

  _expireEstimate() {
    this._clearExpiryTimer()

    const active = this._getActiveIndicator()
    if (active) {
      const dot = active.querySelector("span:first-child")
      const text = active.querySelector("span:last-child")
      if (dot) {
        dot.classList.remove("bg-green-400", "animate-pulse")
        dot.classList.add("bg-amber-400")
      }
      if (text) text.textContent = "Recalculando cotización..."
    }

    const frameId = `${this.activeTabValue}-estimate-preview`
    const frame = document.getElementById(frameId)
    if (frame) {
      const content = frame.querySelector(".rounded-lg")
      if (content) content.classList.add("opacity-50")
    }

    this._setSubmitEnabled(false)
    this.fetchEstimate()
  }

  _clearExpiryTimer() {
    if (this._expiryInterval) {
      clearInterval(this._expiryInterval)
      this._expiryInterval = null
    }
  }

  _setSubmitEnabled(enabled) {
    this.submitBtnTargets.forEach(btn => {
      // Only affect the visible panel's button
      const panel = btn.closest("[data-trading-panel-target]")
      if (!panel) return
      const isActivePanel = (this.activeTabValue === "buy" && panel === this.buyPanelTarget) ||
                            (this.activeTabValue === "sell" && panel === this.sellPanelTarget)
      if (isActivePanel) {
        btn.disabled = !enabled
        btn.classList.toggle("opacity-50", !enabled)
        btn.classList.toggle("cursor-not-allowed", !enabled)
      }
    })
  }

  _getActiveIndicator() {
    const frameId = `${this.activeTabValue}-estimate-preview`
    const frame = document.getElementById(frameId)
    if (!frame) return null
    return frame.querySelector("[data-trading-panel-target='expiryIndicator']")
  }

  // --- Real-time price updates ---

  _onPricesChanged(event) {
    const { probability_yes, probability_no } = event.detail

    // Update probability display on outcome buttons
    this.outcomeTargets.forEach(btn => {
      const outcome = btn.dataset.outcome
      const probSpan = btn.querySelector(".opacity-75")
      if (probSpan && !probSpan.textContent.includes("shares")) {
        const prob = outcome === "yes" ? probability_yes : probability_no
        probSpan.textContent = `${(prob * 100).toFixed(1)}%`
      }
    })

    // Expire current estimate immediately and re-fetch
    this._clearExpiryTimer()
    const active = this._getActiveIndicator()
    if (active) {
      const dot = active.querySelector("span:first-child")
      const text = active.querySelector("span:last-child")
      if (dot) {
        dot.classList.remove("bg-green-400", "animate-pulse")
        dot.classList.add("bg-amber-400")
      }
      if (text) text.textContent = "Precio actualizado, recalculando..."
    }

    const frameId = `${this.activeTabValue}-estimate-preview`
    const frame = document.getElementById(frameId)
    if (frame) {
      const content = frame.querySelector(".rounded-lg")
      if (content) content.classList.add("opacity-50")
    }

    this._setSubmitEnabled(false)

    // Debounce re-fetch to avoid hammering during rapid trades
    if (this._refreshTimer) clearTimeout(this._refreshTimer)
    this._refreshTimer = setTimeout(() => this.fetchEstimate(), 500)
  }

  // --- Helpers ---

  _showEstimateSkeleton() {
    this._clearExpiryTimer()
    const frameId = `${this.activeTabValue}-estimate-preview`
    const frame = document.getElementById(frameId)
    if (!frame) return
    frame.innerHTML = `
      <div class="rounded-lg bg-slate-800 p-4 space-y-3">
        <div class="skeleton h-8 w-32 mx-auto"></div>
        <div class="skeleton h-4 w-full"></div>
        <div class="skeleton h-4 w-3/4"></div>
        <div class="skeleton h-4 w-5/6"></div>
      </div>
    `
  }
}
